import React, { useContext, useEffect, useState } from "react";
import {
  Table,
  Button,
  Typography,
  Tag,
  Dropdown,
  Menu,
  message,
  Input,
  DatePicker,
  Row,
  Col,
  Card,
  Modal,
  Tooltip,
  Form,
  Select,
} from "antd";
import {
  AiFillDelete,
  AiOutlineCheckCircle,
  AiOutlineClockCircle,
  AiOutlineCloseCircle,
} from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { Dayjs } from "dayjs";
import { AuthContext } from "../../Auth/AuthContext";
import {
  deleteLeave,
  editleave,
  getallLeave,
  getSingleUserallLeave,
} from "../../utils/API";
import { RiAddBoxFill } from "react-icons/ri";
import { SearchOutlined } from "@ant-design/icons";
import Applyleavemodal from "./applyleave";
import { FaTrashAlt } from "react-icons/fa";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

// Extend dayjs with the plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Title } = Typography;

type LeaveDataType = {
  startDate: string;
  endDate: string;
  _id: string;
  userId: { _id: string; username: string };
  reason: string;
  leaveType: string;
  status: string;
  leaveReason: string;
  remarks: string;
  highlightRed?: boolean;
  createdAt?: string;
  updatedAt?: string;
  day?: number; // Added to include the dynamically calculated day
};

export const LeavePage = () => {
  const navigate = useNavigate();
  const { authData } = useContext(AuthContext);
  const [form] = Form.useForm();
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [tableData, setTableData] = useState<LeaveDataType[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const { RangePicker } = DatePicker;
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedLeaveId, setSelectedLeaveId] = useState<string | null>(null);
  const isAdmin = authData?.roleId?.roleId === 1;
  const [remark, setRemark] = useState("");

  const statusStyles: { [key: string]: string } = {
    Pending: "bg-yellow-100 text-yellow-800",
    Approved: "bg-green-100 text-green-800",
    Rejected: "bg-red-100 text-red-800",
  };

  const leaveDurationStyles: { [key: string]: string } = {
    "Full Day": "bg-blue-100 text-blue-800", // Blue (unchanged)
    "Half Day": "bg-fuchsia-100 text-fuchsia-800", // Hot pink (replaces purple)
    "Multiple Days": "bg-teal-100 text-teal-800", // Teal (unchanged)
  };

  const leaveTypeStyles: { [key: string]: string } = {
    Sick: "bg-amber-100 text-amber-800", // Orange/yellow (unchanged)
    Emergency: "bg-orange-100 text-orange-800", // Bright orange (replaces rose)
    Casual: "bg-sky-100 text-sky-800", // Light blue (replaces emerald)
  };

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  const currentUser = () => {
    const data = localStorage.getItem("authData");
    if (!data) return null;

    const parsed = JSON.parse(data);
    return {
      ...parsed.user,
      role: parsed.roleId?.name?.toLowerCase() || "",
    };
  };

  const fetchLeaves = async (developerId = null) => {
    setLoading(true);
    try {
      const { role, _id: userId } = currentUser() || {};
      const developerIdToUse =
        developerId || (role === "admin" ? null : userId);

      // Format dates consistently
      const startDateParam = dateRange[0]?.format("DD-MM-YYYY");
      const endDateParam = dateRange[1]?.format("DD-MM-YYYY");

      const response: any = isAdmin
        ? await getallLeave({
            search: searchText,
            page: pagination.page,
            status: selectedStatus,
            developerId: developerIdToUse,
            role,
            startDate: startDateParam,
            endDate: endDateParam,
          })
        : await getSingleUserallLeave({
            search: searchText,
            page: pagination.page,
            status: selectedStatus,
            startDate: startDateParam,
            endDate: endDateParam,
          });

      if (response.data) {
        // Calculate days for each leave record
        const calculateDays = (
          startDate: string,
          endDate: string,
          leaveType: string,
        ) => {
          const type = leaveType?.toLowerCase();
          if (
            type === "half leave" ||
            type === "half-day" ||
            type === "halfday"
          ) {
            return 0.5;
          }

          const start = dayjs(startDate, "DD-MM-YYYY");
          const end = dayjs(endDate, "DD-MM-YYYY");
          return end.diff(start, "day") + 1;
        };

        const filteredData = response.data
          .filter((leave: any) => leave.status !== "Deleted")
          .sort(
            (a: any, b: any) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )
          .map((leave: any, index: number) => ({
            ...leave,
            serialNumber: index + 1,
            day: calculateDays(leave.startDate, leave.endDate, leave.leaveType),
          }));

        setTableData(filteredData);
        setTotalRecords(response.total);
        setPagination((prev) => ({
          ...prev,
          total: response.total,

          page: response.page,
        }));
      } else {
        setTableData([]);
        message.error(response.message || "Failed to fetch leaves");
      }
    } catch (error) {
      console.error("Fetch leaves error:", error);
      message.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, [pagination.page, searchText, dateRange, selectedStatus]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [searchText, dateRange]);

  const changeStatus = async (
    leaveId: string,
    newStatus: string,
    remarks?: string,
  ) => {
    if (!leaveId || !newStatus) {
      message.error("Invalid leave ID or status.");
      return;
    }

    try {
      const payload: any = { status: newStatus };
      if (remarks) {
        payload.remarks = remarks;
      }

      const response = await editleave(leaveId, payload);

      if (
        response?.success ||
        response?.status === 200 ||
        response?.message?.toLowerCase().includes("updated")
      ) {
        message.success("Leave status updated!");
        setTableData((prevData) =>
          prevData.map((item) =>
            item._id === leaveId
              ? { ...item, status: newStatus, remarks: remarks || item.remarks }
              : item,
          ),
        );
        setTimeout(() => {
          fetchLeaves();
        }, 500);
      } else {
        message.error(response?.message || "❌ Failed to update status.");
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Error updating leave.");
    }
  };

  const handleDelete = async () => {
    if (deleteId) {
      try {
        const response = await deleteLeave(deleteId);
        if (response?.success) {
          message.success("Leave deleted successfully");
          setTableData((prevData) =>
            prevData.filter((item) => item._id !== deleteId),
          );
          fetchLeaves();
        } else {
          message.error("Failed to delete leave");
        }
      } catch (error) {
        message.error("Error deleting leave. Please try again.");
      } finally {
        setDeleteId(null);
      }
    }
  };

  const handleDeleteConfirmation = (id: string) => {
    setDeleteId(id);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value.toLowerCase());
  };

  const handleDateChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setDateRange(dates || [null, null]);
  };

  const showRejectModal = (leaveId: string) => {
    setSelectedLeaveId(leaveId);
    setRejectModalVisible(true);
  };

  const handleRejectSubmit = () => {
    if (!selectedLeaveId) return;
    changeStatus(selectedLeaveId, "Rejected", remark);
    setRejectModalVisible(false);
    setRemark("");
  };

  const filteredData = tableData
    .filter((item: LeaveDataType) => {
      const matchesSearch =
        searchText === "" ||
        item.userId?.username?.toLowerCase().includes(searchText) ||
        item.startDate?.toLowerCase().includes(searchText) ||
        item.endDate?.toLowerCase().includes(searchText) ||
        item.reason?.toLowerCase().includes(searchText) ||
        item.status?.toLowerCase().includes(searchText) ||
        item.leaveType?.toLowerCase().includes(searchText) ||
        item.leaveReason?.toLowerCase().includes(searchText);

      return matchesSearch;
    })
    .sort(
      (a, b) =>
        dayjs(b.startDate, "DD-MM-YYYY").valueOf() -
        dayjs(a.startDate, "DD-MM-YYYY").valueOf(),
    );

  const calculateDays = (startDateStr: string, endDateStr: string) => {
    const startDate = dayjs(startDateStr, "DD-MM-YYYY");
    const endDate = dayjs(endDateStr, "DD-MM-YYYY");
    return endDate.diff(startDate, "day") + 1;
  };

  const columns: ColumnsType<LeaveDataType> = [
    {
      title: "#",
      dataIndex: "_id",
      key: "_id",
      render: (_text, _record, index) => index + 1,
    },
    ...(isAdmin
      ? [
          {
            title: "Developer Name",
            dataIndex: "userId",
            key: "userId",
            render: (user: { username: string }) => user?.username || "-",
          },
        ]
      : []),
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
    },
    {
      title: "Days",
      dataIndex: "day",
      key: "day",
      render: (days: number) => {
        let bgColor = "";
        let textColor = "";
        let opacity = "opacity-100";

        if (days === 0.5) {
          bgColor = "bg-[#FF4C4C]/30"; // 🔴 Bright Red
          textColor = "text-[#802626]"; // Strong contrast
        } else if (days === 1) {
          bgColor = "bg-[#FF1493]/30"; // 🌸 Hot Pink / Neon Pink
          textColor = "text-[#80084F]"; // Bright + playful
        } else if (days > 1) {
          bgColor = "bg-[#8B4513]/30"; // 🤎 Saddle Brown (rich earthy tone)
          textColor = "text-[#5C2A0C]"; // Clean readability
        }

        return (
          <span
            className={`px-2 py-0.5 rounded-pill ${bgColor} ${textColor} ${opacity} font-md`}
          >
            {days}
          </span>
        );
      },
    },

    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (text: string) => {
        const maxLength = 20; // length to truncate for ellipsis

        // Only show tooltip if text is longer than maxLength
        if (text.length > maxLength) {
          const shortText = `${text.substring(0, maxLength)}...`;

          return (
            <Tooltip
              title={
                <div className="max-w-xs whitespace-pre-line text-sm">
                  {text}
                </div>
              }
              color="#CCCCCC"
              overlayInnerStyle={{ color: "#000", padding: "12px" }}
              placement="topLeft"
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  maxWidth: "140px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                }}
              >
                {shortText}
              </span>
            </Tooltip>
          );
        }

        // For short text, just render normally
        return <span>{text}</span>;
      },
    },
    {
      title: "Leave Duration",
      dataIndex: "leaveType",
      key: "leaveType",
      render: (leaveType: string, record: LeaveDataType) => {
        const normalizedLeaveType = leaveType?.toLowerCase();
        const days =
          record.day !== undefined
            ? record.day
            : calculateDays(record.startDate, record.endDate);
        const displayLeaveType =
          normalizedLeaveType === "half leave" ||
          normalizedLeaveType === "half-day" ||
          normalizedLeaveType === "halfday"
            ? "Half Day"
            : days > 1
              ? "Multiple Days"
              : "Full Day";
        return (
          <span
            className={`badge rounded-pill ${leaveDurationStyles[displayLeaveType] || "bg-gray-100 text-gray-800"}`}
          >
            {displayLeaveType}
          </span>
        );
      },
    },
    {
      title: "Leave Type",
      dataIndex: "leaveReason",
      key: "leaveReason",
      render: (leaveReason: string) => {
        const normalizedLeaveReason = leaveReason || "Unknown";
        return (
          <span
            className={`badge rounded-pill ${leaveTypeStyles[normalizedLeaveReason] || "bg-gray-100 text-gray-800"}`}
          >
            {normalizedLeaveReason}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string, record: LeaveDataType) => {
        const getStatusBadge = (value: string) => (
          <span className={`badge rounded-pill ${statusStyles[value]}`}>
            {value}
          </span>
        );

        if (isAdmin) {
          const handleMenuClick = ({ key }: { key: string }) => {
            if (key === "Rejected") {
              showRejectModal(record._id);
            } else {
              changeStatus(record._id, key);
            }
          };

          return (
            <Dropdown
              overlay={
                <Menu onClick={handleMenuClick}>
                  {["Pending", "Approved", "Rejected"].map((item) => (
                    <Menu.Item key={item}>{getStatusBadge(item)}</Menu.Item>
                  ))}
                </Menu>
              }
              trigger={["click"]}
            >
              <span className="cursor-pointer">{getStatusBadge(status)}</span>
            </Dropdown>
          );
        }

        return status === "Rejected" && record.remarks ? (
          <Tooltip
            title={record.remarks}
            placement="top"
            overlayInnerStyle={{
              backgroundColor: "#fff",
              color: "#000",
              borderRadius: "8px",
              padding: "8px 12px",
              fontSize: "14px",
              fontWeight: 500,
              maxWidth: "240px",
              whiteSpace: "normal",
              textAlign: "center",
            }}
            color="#CCCCCC"
          >
            <span>{getStatusBadge(status)}</span>
          </Tooltip>
        ) : (
          getStatusBadge(status)
        );
      },
    },
    ...(!isAdmin
      ? [
          {
            title: "Action",
            key: "action",
            render: (record: LeaveDataType) => {
              if (record.status !== "Pending") return null;
              return (
                <Tooltip title="Delete">
                  <button
                    onClick={() => handleDeleteConfirmation(record._id)}
                    className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 
                      shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
                      hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
                      active:translate-y-1 active:shadow-none 
                      text-white flex items-center justify-center 
                      transition-all duration-200 group"
                  >
                    <FaTrashAlt
                      className="group-hover:scale-110 transition-transform"
                      size={12}
                    />
                  </button>
                </Tooltip>
              );
            },
          },
        ]
      : []),
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  return (
    <div>
      <Row
        className="m-2"
        align="middle"
        justify="space-between"
        style={{ flexWrap: "wrap" }}
      >
        {/* Title */}
        <Col xs={24} sm={24} md={6} lg={5} xl={4}>
          <h2 className="headername">Leaves</h2>
        </Col>
        {isAdmin ? (
          <Col
            xs={24}
            sm={24}
            md={18}
            lg={19}
            xl={20}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            {/* Status Select */}
            <div style={{ flex: "1 1 150px", maxWidth: "200px" }}>
              <Select
                style={{ width: "100%", height: "33px" }}
                placeholder="Select status"
                value={selectedStatus}
                onChange={(value) => {
                  setSelectedStatus(value || null);
                  setPage(1);
                }}
                allowClear
                size="large"
                options={[
                  { value: "Approved", label: "Approved" },
                  { value: "Pending", label: "Pending" },
                  { value: "Rejected", label: "Rejected" },
                ]}
              />
            </div>

            {/* Date Range Picker */}
            <div style={{ flex: "1 1 250px", maxWidth: "280px" }}>
              <RangePicker
                onChange={handleDateChange}
                placeholder={["Start Date", "End Date"]}
                format="DD-MM-YYYY"
                size="large"
                value={dateRange}
                allowClear
                style={{ width: "100%", height: "33px" }}
              />
            </div>

            {/* Search Input */}
            <div style={{ flex: "1 1 200px", maxWidth: "250px" }}>
              <Input
                prefix={<SearchOutlined />}
                size="large"
                placeholder="Search..."
                allowClear
                value={searchText}
                onChange={handleSearch}
                style={{ width: "100%", height: "33px" }}
              />
            </div>
          </Col>
        ) : (
          <Col
            xs={24}
            sm={12}
            md={12}
            lg={8}
            xl={12}
            className="flex justify-end"
          >
            <Button
              type="primary"
              onClick={() => setIsAddTaskModalOpen(true)}
              className="allbuttons"
            >
              <RiAddBoxFill className="button-icon" />
              <div className="button-text">Apply leave</div>
            </Button>

            <Applyleavemodal
              visible={isAddTaskModalOpen}
              onClose={() => setIsAddTaskModalOpen(false)}
              onTaskCreated={() => {
                setIsAddTaskModalOpen(false);
                fetchLeaves();
              }}
              refreshLeaves={fetchLeaves}
            />
          </Col>
        )}
      </Row>

      <Card style={{ borderRadius: 10 }}>
        <div
          className="scrollbar-hide custom-scroll-table"
          style={{ overflowX: "auto" }}
        >
          <Table
            rowClassName={(record) =>
              record.highlightRed ? "bg-red-100 text-red-700" : ""
            }
            columns={columns}
            dataSource={filteredData}
            scroll={{ x: "max-content" }}
            rowKey={(record) => record._id}
            loading={loading}
            pagination={{
              pageSize: pagination.pageSize,
              current: pagination.page,
              onChange: (page) => setPagination((prev) => ({ ...prev, page })),
              showSizeChanger: false,
              total: pagination.total,
            }}
          />
        </div>
      </Card>
      <Modal
        open={!!deleteId}
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Leave?</p>
      </Modal>

      <Modal
        visible={rejectModalVisible}
        onOk={handleRejectSubmit}
        onCancel={() => setRejectModalVisible(false)}
        footer={null}
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: 20,
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20, // Optional: Adjust spacing
            }}
          >
            Enter Rejection Remark
          </div>
        }
        bodyStyle={{
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
      >
        <div className="elements">
          <Form layout="vertical">
            <Form.Item
              label={
                <span className="Inputlabel">
                  Remark <span style={{ color: "red" }}>*</span>
                </span>
              }
              required={false}
            >
              <Input.TextArea
                rows={4}
                placeholder="Enter remark"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                className="Allinputtextarea"
              />
            </Form.Item>
          </Form>
        </div>

        <div className="footer-modals" />
        <Row justify="end">
          <Col
            xs={24}
            sm={24}
            md={24}
            xl={24}
            xxl={20}
            className="d-flex justify-content-end me-3"
          >
            <div className="button-wrapper">
              <button
                className="allcanclebutton"
                onClick={() => setRejectModalVisible(false)}
                type="button"
              >
                Cancel
              </button>

              <button
                onClick={handleRejectSubmit}
                className="allsubmitebutton"
                type="submit"
              >
                Submit
              </button>
            </div>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export const LEAVES_TABLE_COLUMNS = [
  "userId",
  "startDate",
  "endDate",
  "day",
  "reason",
  "leaveType",
  "leaveReason",
  "status",
];
